import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  ActionsGetWorkflowResponseData,
  ActionsListWorkflowRunsForRepoResponseData,
  CloudbuildApi,
} from '@backstage-community/plugin-cloudbuild';

/**
 * Implementação de `CloudbuildApi` que fala com a API do Cloud Build
 * através do backend `gcp-cloudbuild-proxy` (F-0030) em vez do fluxo
 * padrão do plugin, que exige login com Google (`googleAuthApiRef`) — o
 * Myralis usa Microsoft Entra/guest, não Google. Ver design.md D1/D2.
 *
 * Somente leitura: `reRunWorkflow` não é suportado — a service account por
 * trás do proxy só tem `roles/cloudbuild.viewer` (D3).
 */
export class ProxiedCloudbuildClient implements CloudbuildApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  private async getBaseUrl(): Promise<string> {
    return this.options.discoveryApi.getBaseUrl('gcp-cloudbuild-proxy');
  }

  private async request(path: string): Promise<any> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.options.fetchApi.fetch(`${baseUrl}${path}`);
    if (!response.ok) {
      throw new Error(
        `Falha ao chamar o proxy do Cloud Build (${response.status}): ${await response.text()}`,
      );
    }
    return response.json();
  }

  async listWorkflowRuns(options: {
    projectId: string;
    location: string;
    cloudBuildFilter: string;
  }): Promise<ActionsListWorkflowRunsForRepoResponseData> {
    const { projectId, location, cloudBuildFilter } = options;
    return this.request(
      `/v1/projects/${encodeURIComponent(projectId)}/locations/${encodeURIComponent(
        location,
      )}/builds?filter=${encodeURIComponent(cloudBuildFilter)}`,
    );
  }

  async getWorkflow(options: {
    projectId: string;
    location: string;
    id: string;
  }): Promise<ActionsGetWorkflowResponseData> {
    const { projectId, location, id } = options;
    return this.request(
      `/v1/projects/${encodeURIComponent(projectId)}/locations/${encodeURIComponent(
        location,
      )}/builds/${encodeURIComponent(id)}`,
    );
  }

  async getWorkflowRun(options: {
    projectId: string;
    location: string;
    id: string;
  }): Promise<ActionsGetWorkflowResponseData> {
    return this.getWorkflow(options);
  }

  async reRunWorkflow(): Promise<void> {
    throw new Error(
      'Re-executar build não é suportado: o proxy do Cloud Build é somente leitura (F-0030 D3).',
    );
  }
}
